import { useCallback, useMemo, useState } from 'react';
import { useGetEventMetricsQuery } from '@store/api/eventService/eventServiceApi';
import type { EventMetricsResult, MetricsPeriodInput, MetricsRequestParams } from '@store/models';

/** Index of a period slot: 0 = primary, 1 = optional comparison. */
export type MetricsPeriodSlot = 0 | 1;

/** Public shape returned by {@link usePlannerMetrics}. */
export interface UsePlannerMetricsResult {
  /** Currently selected building filter (empty means all buildings). */
  buildingIds: string[];
  /** Active periods (1 primary, optionally a 2nd comparison period). */
  periods: MetricsPeriodInput[];
  /** Replaces the building filter selection. */
  setBuildingIds: (ids: string[]) => void;
  /** Updates the period at the given slot. */
  setPeriod: (slot: MetricsPeriodSlot, period: MetricsPeriodInput) => void;
  /** Adds an empty second (comparison) period if not already present. */
  addComparisonPeriod: () => void;
  /** Removes the second (comparison) period. */
  removeComparisonPeriod: () => void;
  /** True when a second comparison period is active. */
  hasComparison: boolean;
  /** True when the current selection is complete enough to query. */
  canQuery: boolean;
  /** Server-computed metrics result, when available. */
  data?: EventMetricsResult;
  /** True during the initial load. */
  isLoading: boolean;
  /** True during any in-flight fetch (including refetch). */
  isFetching: boolean;
  /** True when the query failed. */
  isError: boolean;
  /** Re-runs the metrics query with the current selection. */
  refetch: () => void;
}

const EMPTY_PERIOD: MetricsPeriodInput = { startDate: '', endDate: '' };

function isPeriodComplete(period: MetricsPeriodInput | undefined): boolean {
  if (!period) return false;
  if (!period.startDate || !period.endDate) return false;
  return period.startDate <= period.endDate;
}

/**
 * Owns the planner metrics report selection state (buildings + 1–2 periods) and
 * wraps the `getEventMetrics` RTK Query call, skipping until the primary period
 * is complete. All metric values remain server-computed (Constitution XIV).
 */
export function usePlannerMetrics(): UsePlannerMetricsResult {
  const [buildingIds, setBuildingIds] = useState<string[]>([]);
  const [periods, setPeriods] = useState<MetricsPeriodInput[]>([{ ...EMPTY_PERIOD }]);

  const setPeriod = useCallback((slot: MetricsPeriodSlot, period: MetricsPeriodInput) => {
    setPeriods((prev) => {
      const next = [...prev];
      next[slot] = period;
      return next;
    });
  }, []);

  const addComparisonPeriod = useCallback(() => {
    setPeriods((prev) => (prev.length >= 2 ? prev : [...prev, { ...EMPTY_PERIOD }]));
  }, []);

  const removeComparisonPeriod = useCallback(() => {
    setPeriods((prev) => (prev.length > 1 ? prev.slice(0, 1) : prev));
  }, []);

  const hasComparison = periods.length > 1;

  // Only send complete periods; the primary must be complete to query at all.
  const activePeriods = useMemo(() => periods.filter(isPeriodComplete), [periods]);
  const canQuery = isPeriodComplete(periods[0]) && activePeriods.length === periods.length;

  const requestParams = useMemo<MetricsRequestParams>(
    () => ({
      ...(buildingIds.length > 0 ? { buildingIds } : {}),
      periods: activePeriods,
    }),
    [buildingIds, activePeriods]
  );

  const { data, isLoading, isFetching, isError, refetch } = useGetEventMetricsQuery(requestParams, {
    skip: !canQuery,
  });

  const refetchMetrics = useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    buildingIds,
    periods,
    setBuildingIds,
    setPeriod,
    addComparisonPeriod,
    removeComparisonPeriod,
    hasComparison,
    canQuery,
    data,
    isLoading,
    isFetching,
    isError,
    refetch: refetchMetrics,
  };
}
